import type { SSEError, SSEStreamCallbacks } from "@/hooks/useApi";
import { withoutBrief } from "@/lib/company-tag";
import {
  companyPreviewLookupKey,
  type CompanyPreviewRequestDTO,
  type CompanyPreviewStreamEvent,
  type CompanyTag,
  type CompanyWebHighlight,
  type CompanyWebKeyData,
  type CompanyWebProfileDTO,
  type CompanyWebProfilePartialDTO,
  type CompanyWebSource,
} from "@shaarp/common";

export type CompanyWebPreviewPhase = "loading" | "streaming" | "done" | "error";

/** Where the run stands, from the latest `progress` event. */
export type CompanyWebPreviewProgress = Omit<
  Extract<CompanyPreviewStreamEvent, { step: "progress" }>,
  "step"
>;

export type CompanyWebPreviewState = {
  phase: CompanyWebPreviewPhase;
  /** Set by the first event of the stream; carries the company already in base. */
  resolved?: { key: string; companyId: string | null; cached: boolean };
  /** Latest progress of the run; absent for a cached profile. */
  progress?: CompanyWebPreviewProgress;
  /** The profile as the model writes it, until `done` brings the stored one. */
  partial?: CompanyWebProfilePartialDTO;
  profile?: CompanyWebProfileDTO;
  errorCode?: string;
  /**
   * The company created from the panel once the run had none in base: it backs
   * the tag from then on, whatever the stream said when it resolved.
   */
  createdCompanyId?: string;
};

/** The `post` of `useApiStream()`, handed in so the headers stay the hook's. */
export type CompanyWebPreviewPost = (
  uri: string,
  data: Record<string, unknown>,
  callbacks: SSEStreamCallbacks,
) => Promise<void>;

// Session-long cache: bounded so a long session cannot grow it without limit.
const MAX_ENTRIES = 100;

/**
 * The key a tag's web synthesis is filed under for `teamId`: the tag's
 * identity (`companyPreviewLookupKey`), never coarser than the one the server
 * stores the profile under, behind the team. The run answers with the company
 * the team holds, and switching teams does not reload the page: without the
 * team, a chip would keep serving the previous team's company.
 */
export function companyWebPreviewKey(
  teamId: string | undefined,
  tag: CompanyTag,
): string {
  // A tag with no name left to key on is refused by the server as `invalid_tag`.
  const identity = companyPreviewLookupKey(tag) ?? `invalid:${tag.name}`;
  return `team:${teamId ?? ""}|${identity}`;
}

const states = new Map<string, CompanyWebPreviewState>();
/** The request each key listens to; an older one's late events are dropped. */
const latestRun = new Map<string, object>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function setState(key: string, state: CompanyWebPreviewState): void {
  states.set(key, state);
  emit();
}

/** A run still loading or streaming its profile. */
export function isRunInProgress(state: CompanyWebPreviewState): boolean {
  return state.phase === "loading" || state.phase === "streaming";
}

/**
 * The key data of a settled `ok` profile, else `null`: one being written may
 * still drop a figure, or hold half a SIRET.
 */
export function settledWebKeyData(
  state: CompanyWebPreviewState,
): CompanyWebKeyData | null {
  return state.phase === "done" && state.profile?.status === "ok"
    ? state.profile.keyData
    : null;
}

/**
 * The synthesis of a run: the written one, else what the model has written of
 * it so far. `awaited` while the run goes and the model has not said it found
 * homonyms or nothing instead.
 */
export function webSynthesisOf(state: CompanyWebPreviewState): {
  synthesis: string | null;
  isWriting: boolean;
  awaited: boolean;
} {
  const { profile, partial } = state;
  const isWriting = isRunInProgress(state);
  const synthesis =
    (profile?.status === "ok" ? profile.synthesis : null) ??
    (isWriting ? partial?.synthesis : null);
  return {
    synthesis: synthesis || null,
    isWriting,
    awaited: isWriting && (!partial?.status || partial.status === "ok"),
  };
}

/** The takeaways of a run: the ones written so far while it goes, else the profile's. */
export function webHighlightsOf(
  state: CompanyWebPreviewState,
): CompanyWebHighlight[] {
  const { profile, partial } = state;
  if (isRunInProgress(state)) {
    return partial?.status === "ok" ? partial.highlights : [];
  }
  return state.phase === "done" && profile?.status === "ok"
    ? profile.highlights
    : [];
}

/**
 * The pages a finished profile read, with their titles: what its takeaways'
 * footnotes are named by. None while the run writes.
 */
export function webSourcesOf(
  state: CompanyWebPreviewState,
): CompanyWebSource[] {
  const { profile } = state;
  return state.phase === "done" && profile?.status === "ok"
    ? profile.sources
    : [];
}

/**
 * Whether a run ended without a profile to show — an error, nothing found, or
 * namesakes it could not tell apart: a page that keeps its own layout says so
 * (`CompanyWebProfileNotice`) rather than show nothing.
 */
export function hasWebProfileNotice(state: CompanyWebPreviewState): boolean {
  if (state.phase === "error") return true;
  return state.phase === "done" && !!state.profile && state.profile.status !== "ok";
}

/** A run that ended, with a profile or an error. */
function isFinished(state: CompanyWebPreviewState): boolean {
  return state.phase === "done" || state.phase === "error";
}

/**
 * Drops the oldest finished entry once full; a running request is never
 * evicted. Its subscribers are told, so a panel still showing it starts it
 * again rather than waiting on an entry that is gone.
 */
function evictIfFull(): void {
  if (states.size < MAX_ENTRIES) return;
  for (const [key, state] of states) {
    if (isFinished(state)) {
      states.delete(key);
      latestRun.delete(key);
      emit();
      return;
    }
  }
}

function applyEvent(
  current: CompanyWebPreviewState,
  event: CompanyPreviewStreamEvent,
): CompanyWebPreviewState {
  switch (event.step) {
    case "resolved":
      return {
        ...current,
        resolved: {
          key: event.key,
          companyId: event.companyId,
          cached: event.cached,
        },
      };
    case "progress":
      // A late event never rewinds a finished or failed run, nor the counts.
      if (isFinished(current)) return current;
      return {
        ...current,
        progress: {
          phase: event.phase,
          pagesFound: Math.max(event.pagesFound, current.progress?.pagesFound ?? 0),
          pagesRead: Math.max(event.pagesRead, current.progress?.pagesRead ?? 0),
        },
      };
    case "partial":
      return { ...current, phase: "streaming", partial: event.profile };
    case "done":
      return { ...current, phase: "done", profile: event.profile };
    case "error":
      return { ...current, phase: "error", errorCode: event.code };
    default:
      return current;
  }
}

/**
 * A run that failed and may succeed if the person asks again: an `unavailable`
 * error, the stored `error` profile, or an empty fiche the server marks
 * `provisional` (some searches failed, or its checks emptied it). An invalid
 * tag stays invalid however often it is sent, and a spent budget is not
 * refilled by a click: the button would only feed the 429.
 */
export function isRetryableFailure(state: CompanyWebPreviewState): boolean {
  if (state.phase === "error") {
    return (
      state.errorCode !== "invalid_tag" && state.errorCode !== "rate_limited"
    );
  }
  if (state.phase !== "done" || !state.profile) return false;
  return (
    state.profile.status === "error" ||
    (state.profile.status === "none" && state.profile.provisional)
  );
}

/**
 * A failed request that reopening the panel sends again: a retryable one, or a
 * refusal for a spent budget, which may have been refilled since. Not an
 * invalid tag, and not the stored `error` profile — the server would serve it
 * again; only `retry` gets past it.
 */
function isRerunOnReopen(state: CompanyWebPreviewState): boolean {
  if (state.phase !== "error") return false;
  return isRetryableFailure(state) || state.errorCode === "rate_limited";
}

/** The code of a request refused before it streamed. */
function refusalCode(error: SSEError): string {
  if (error.code) return error.code;
  return error.status === 429 ? "rate_limited" : "unavailable";
}

function isStreamEvent(value: unknown): value is CompanyPreviewStreamEvent {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { step?: unknown }).step === "string"
  );
}

/**
 * One request for `key`, from loading to its end. Only the latest request of a
 * key writes to it: the stream of a run that was retried may still close after
 * the retry started, and must not fail it. A company created from the panel
 * outlives the run it was created on: a new run of the key keeps it.
 */
async function run(
  key: string,
  body: CompanyPreviewRequestDTO,
  post: CompanyWebPreviewPost,
): Promise<void> {
  const createdCompanyId = states.get(key)?.createdCompanyId;
  // A key already held is replaced in place: nothing to make room for.
  if (!states.has(key)) evictIfFull();
  const token = {};
  latestRun.set(key, token);
  setState(key, {
    phase: "loading",
    ...(createdCompanyId && { createdCompanyId }),
  });

  const update = (change: (current: CompanyWebPreviewState) => CompanyWebPreviewState) => {
    const current = states.get(key);
    if (current && latestRun.get(key) === token) setState(key, change(current));
  };
  const fail = (code: string) =>
    update((current) =>
      isFinished(current)
        ? current
        : { ...current, phase: "error", errorCode: code },
    );

  await post("/company/preview", body, {
    onMessage: ({ data }) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      if (!isStreamEvent(parsed)) return;
      update((current) => applyEvent(current, parsed));
    },
    // A stream that ends with neither `done` nor `error` is a failed run.
    onClose: () => fail("unavailable"),
    // A refusal before streaming carries the API's `code` (SSEError); the
    // throttler's 429 comes before the route and carries none.
    onError: (error) => fail(refusalCode(error as SSEError)),
  });
}

/**
 * Module-level store of the web synthesis a company panel shows, keyed by the
 * team and the tag's identity (`companyWebPreviewKey`).
 *
 * One request per key: two panels on the same company share it. It outlives the
 * panel — closing it, or stepping to another company, never aborts the request
 * (the server finishes the run either way), and reopening the company shows the
 * finished result without a new call. A failed request is the only one
 * `start` runs again, so reopening the panel retries it — unless the tag was
 * refused as invalid; `retry` is the person asking again, and gets past the
 * failure the server stored.
 */
export const companyWebPreviewStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getState(key: string): CompanyWebPreviewState | undefined {
    return states.get(key);
  },
  async start(
    key: string,
    tag: CompanyTag,
    post: CompanyWebPreviewPost,
  ): Promise<void> {
    const existing = states.get(key);
    if (existing && !isRerunOnReopen(existing)) return;
    await run(key, { tag: withoutBrief(tag) }, post);
  },
  async retry(
    key: string,
    tag: CompanyTag,
    post: CompanyWebPreviewPost,
  ): Promise<void> {
    const existing = states.get(key);
    if (!existing || !isRetryableFailure(existing)) return;
    await run(key, { tag: withoutBrief(tag), retry: true }, post);
  },
  /** Records the company created for `key`, so reopening the panel shows it. */
  attachCompany(key: string, companyId: string): void {
    const existing = states.get(key);
    if (!existing) return;
    setState(key, { ...existing, createdCompanyId: companyId });
  },
};
